import { CommonModule } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  HostListener,
  OnDestroy,
  computed,
  effect,
  inject,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { finalize, switchMap } from 'rxjs';

import { InventoryArticle } from '../../models/inventory-article.model';
import { InventoryUnitOption } from '../../models/inventory-unit-option.model';
import { InventoryDetail } from '../../models/inventory-detail.model';
import { InventoryStatus } from '../../models/inventory-status.enum';
import {
  EINHEIT_KUERZEL,
  InventurEinheit,
} from '../../models/inventur-einheit';
import { InventurKatalogArtikel } from '../../models/inventur-katalog-artikel.model';
import { InventoryService } from '../../services/inventory.service';
import { AuthService } from '../../../../core/services/auth.service';

type AnsichtFilter = 'ALLE' | 'OFFEN' | 'ERFASST';

type SortColumn = 'warengruppe' | 'artikelnummer' | 'artikelname';
type SortDirection = 'asc' | 'desc';

interface SortState {
  readonly column: SortColumn | null;
  readonly direction: SortDirection;
}

const ALLE = 'ALLE';

const SUMMARY_SCROLL_TOLERANCE = 4;

const NUMPAD_BACKSPACE = '⌫';

const NUMPAD_KEYS: readonly (string | null)[] = [
  '7',
  '8',
  '9',
  '4',
  '5',
  '6',
  '1',
  '2',
  '3',
  null,
  '0',
  NUMPAD_BACKSPACE,
];

interface UndoUpdateEntry {
  readonly kind: 'update';
  readonly artikelId: string;
  readonly istBestand: number | null;
  readonly sollBestand: number;
  readonly inventurEinheit: InventurEinheit;
  readonly inventurEinheitId: number;
  readonly verkaufspreis: number;
  readonly umrechnungsfaktor: number;
  readonly letzteAenderungZeit: string | null;
  readonly letzteAenderungBenutzer: string | null;
  readonly previousNewlyAddedArticleId: string | null;
}

interface UndoCreateEntry {
  readonly kind: 'create';
  readonly artikelId: string;
  readonly previousNewlyAddedArticleId: string | null;
}

interface UndoBatchEntry {
  readonly kind: 'batch';
  readonly entries: readonly UndoUpdateEntry[];
  readonly selectedIds: readonly string[];
  readonly previousNewlyAddedArticleId: string | null;
}

type UndoEntry = UndoUpdateEntry | UndoCreateEntry | UndoBatchEntry;

@Component({
  selector: 'app-inventur-erfassung',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './inventur-erfassung.component.html',
  styleUrl: './inventur-erfassung.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InventurErfassungComponent implements OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly inventoryService = inject(InventoryService);
  private readonly destroyRef = inject(DestroyRef);

  private lockedInventoryId: string | null = null;
  private readonly filialNummer = Number(this.route.snapshot.paramMap.get('filialNummer'));
  private readonly pendingSaveArtikelIds = new Set<string>();
  private saveInFlight = false;

  protected readonly isSavingPositions = signal(false);

  private readonly tableBody =
    viewChild<ElementRef<HTMLElement>>('tableBody');

  private readonly summaryList =
    viewChild<ElementRef<HTMLElement>>('summaryList');

  protected readonly isPosSession = inject(AuthService).isPosSession();

  protected readonly numpadKeys = NUMPAD_KEYS;
  protected readonly numpadBackspace = NUMPAD_BACKSPACE;

  protected readonly detail = signal<InventoryDetail | null>(null);
  protected readonly artikel = signal<InventoryArticle[]>([]);
  protected readonly isLoading = signal(true);
  protected readonly loadError = signal(false);

  protected readonly skeletonKpiCards = Array.from({ length: 3 });
  protected readonly skeletonRows = Array.from({ length: 8 });
  protected readonly skeletonCells = Array.from({ length: 12 });

  protected readonly searchTerm = signal('');
  protected readonly ansichtFilter = signal<AnsichtFilter>('ALLE');
  protected readonly filterEinheit = signal(ALLE);
  protected readonly isCompact = signal(false);

  protected readonly isEinheitDropdownOpen = signal(false);

  protected readonly isAddOpen = signal(false);
  protected readonly addQuery = signal('');
  protected readonly katalogTreffer = computed(() => {
    const vorhandeneArtikelnummern = new Set(
      this.artikel().map((entry) => entry.artikelnummer)
    );

    return this.inventoryService
      .searchKatalog(this.addQuery())
      .filter(
        (entry) =>
          !vorhandeneArtikelnummern.has(entry.artikelnummer)
      );
  });

  protected readonly sortState = signal<SortState>({
    column: null,
    direction: 'asc',
  });

  protected readonly selection = signal<ReadonlySet<string>>(new Set<string>());

  protected readonly activeId = signal<string | null>(null);
  protected readonly activeUnit = signal<InventurEinheit>('');
  protected readonly inputValue = signal('');
  protected readonly placeholderValue = signal('');
  protected readonly flashId = signal<string | null>(null);
  protected readonly unitMetaFlashId = signal<string | null>(null);
  protected readonly newlyAddedArticleId = signal<string | null>(null);

  private readonly undoStack = signal<readonly UndoEntry[]>([]);

  protected readonly isSummaryOpen = signal(false);
  protected readonly summaryReviewed = signal(false);
  protected readonly isClosing = signal(false);
  protected readonly closeError = signal(false);

  protected readonly einheitOptionen = computed(() => {
    const einheiten = [...new Set(
      this.artikel()
        .map((entry) => entry.inventurEinheit.trim())
        .filter((einheit) => einheit.length > 0)
    )].sort((a, b) => a.localeCompare(b, 'de', { sensitivity: 'base', numeric: true }));

    return [
      { value: ALLE, label: 'Alle Einheiten' },
      ...einheiten.map((einheit) => ({
        value: einheit,
        label: einheit,
      })),
    ];
  });

  protected readonly selectedEinheitLabel = computed(
    () =>
      this.einheitOptionen().find(
        (option) => option.value === this.filterEinheit()
      )?.label ?? 'Alle Einheiten'
  );

  protected readonly gefilterteArtikel = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const ansicht = this.ansichtFilter();
    const einheit = this.filterEinheit();

    const gefiltert = this.artikel().filter((entry) => {
      if (
        term &&
        !entry.artikelname.toLowerCase().includes(term) &&
        !entry.artikelnummer.toLowerCase().includes(term) &&
        !entry.warengruppe.toLowerCase().includes(term)
      ) {
        return false;
      }

      if (einheit !== ALLE && entry.inventurEinheit !== einheit) {
        return false;
      }

      if (!this.matchesAnsicht(entry, ansicht)) {
        return false;
      }

      return true;
    });

    return this.sortArtikel(gefiltert);
  });

  protected readonly anzahlArtikel = computed(() => this.artikel().length);

  protected readonly anzahlErfasst = computed(
    () => this.artikel().filter((entry) => entry.istBestand !== null).length
  );

  protected readonly anzahlOffen = computed(
    () => this.anzahlArtikel() - this.anzahlErfasst()
  );

  protected readonly gesamtwert = computed(() =>
    this.artikel().reduce((sum, entry) => sum + this.artikelWert(entry), 0)
  );

  protected readonly canUndo = computed(() => this.undoStack().length > 0);

  protected readonly fortschritt = computed(() => {
    const gesamt = this.anzahlArtikel();

    return gesamt === 0
      ? 0
      : Math.round((this.anzahlErfasst() / gesamt) * 100);
  });

  protected readonly isReadOnly = computed(
    () => this.detail()?.status === InventoryStatus.Abgeschlossen
  );

  protected readonly canFinish = computed(
    () =>
      !this.isReadOnly() &&
      !this.isSavingPositions() &&
      this.anzahlErfasst() > 0
  );

  protected readonly erfassteArtikel = computed(() =>
    this.artikel().filter((entry) => entry.istBestand !== null)
  );

  protected readonly canConfirmClose = computed(
    () =>
      this.canFinish() &&
      (this.summaryReviewed() || this.erfassteArtikel().length === 0) &&
      !this.isClosing()
  );

  protected readonly selectionCount = computed(() => this.selection().size);

  protected readonly selektierbareArtikel = computed(() =>
    this.gefilterteArtikel().filter((entry) => !this.istErfasst(entry))
  );

  protected readonly areAllFilteredSelected = computed(() => {
    const selectable = this.selektierbareArtikel();
    const selection = this.selection();

    return (
      selectable.length > 0 &&
      selectable.every((entry) => selection.has(entry.id))
    );
  });

  protected readonly activeArtikel = computed(() => {
    const id = this.activeId();

    return id === null
      ? null
      : (this.artikel().find((entry) => entry.id === id) ?? null);
  });

  protected readonly activeUnitOption = computed(() => {
    const active = this.activeArtikel();

    return active === null
      ? null
      : this.findUnitOption(active, this.activeUnit());
  });

  constructor() {
    effect((onCleanup) => {
      const element = this.summaryList()?.nativeElement;

      if (!element) {
        return;
      }

      this.erfassteArtikel();

      untracked(() => this.syncSummaryReviewed(element));

      const observer = new ResizeObserver(() =>
        this.syncSummaryReviewed(element)
      );

      observer.observe(element);

      const content = element.firstElementChild;

      if (content) {
        observer.observe(content);
      }

      onCleanup(() => observer.disconnect());
    });

    const id = this.route.snapshot.paramMap.get('inventoryId');

    if (!id || !Number.isInteger(this.filialNummer) || this.filialNummer <= 0) {
      this.isLoading.set(false);
      this.loadError.set(true);
      void this.router.navigate(['/inventur']);
      return;
    }

    this.openInventoryWithLock(id);
  }

  @HostListener('document:keydown.escape')
  protected onEscape(): void {
    this.isEinheitDropdownOpen.set(false);

    if (this.isAddOpen()) {
      this.closeAddDialog();
      return;
    }

    if (this.isSummaryOpen()) {
      this.closeSummary();
      return;
    }

    this.closeNumpad();
  }

  @HostListener('document:keydown.control.z', ['$event'])
  @HostListener('document:keydown.meta.z', ['$event'])
  protected onUndoShortcut(event: KeyboardEvent): void {
    event.preventDefault();
    this.undo();
  }

  @HostListener('document:keydown.arrowdown', ['$event'])
  protected onArrowDown(event: KeyboardEvent): void {
    this.moveActiveRow(event, 1);
  }

  @HostListener('document:keydown.arrowup', ['$event'])
  protected onArrowUp(event: KeyboardEvent): void {
    this.moveActiveRow(event, -1);
  }

  @HostListener('document:click', ['$event'])
  protected onDocumentClick(event: MouseEvent): void {
    const target = event.target;

    if (!(target instanceof Node)) {
      return;
    }

    const element =
      target instanceof Element
        ? target
        : target.parentElement;

    if (element?.closest('.select-field')) {
      return;
    }

    this.isEinheitDropdownOpen.set(false);
  }

  protected goBack(): void {
    this.releaseLockAndNavigate();
  }

  ngOnDestroy(): void {
    const id = this.lockedInventoryId;

    if (id === null) {
      return;
    }

    this.lockedInventoryId = null;
    this.inventoryService.releaseInventoryLock(id, this.filialNummer).subscribe({
      error: () => this.inventoryService.clearInventoryLock(id, this.filialNummer),
    });
  }

  @HostListener('window:beforeunload')
  protected releaseInventoryLockBeforeUnload(): void {
    const id = this.lockedInventoryId;

    if (id === null) {
      return;
    }

    this.lockedInventoryId = null;
    this.inventoryService.releaseInventoryLockBeforeUnload(id, this.filialNummer);
  }

  protected setSearchTerm(value: string): void {
    this.searchTerm.set(value);
  }

  protected setAnsichtFilter(value: AnsichtFilter): void {
    this.ansichtFilter.set(value);
  }

  protected isAnsichtActive(value: AnsichtFilter): boolean {
    return this.ansichtFilter() === value;
  }

  protected toggleEinheitDropdown(): void {
    this.isEinheitDropdownOpen.update((state) => !state);
  }

  protected openAddDialog(): void {
    if (this.isReadOnly()) return;

    this.isEinheitDropdownOpen.set(false);
    this.closeNumpad();
    this.addQuery.set('');
    this.isAddOpen.set(true);

    const id = this.detail()?.id;

    if (!id) {
      return;
    }

    // Load the catalog only when the user opens "Artikel hinzufügen".
    // Saving an inventory value must not refresh this endpoint.
    this.inventoryService
      .loadAvailableArticles(id, this.filialNummer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({ error: () => undefined });
  }

  protected closeAddDialog(): void {
    this.isAddOpen.set(false);
  }

  protected setAddQuery(value: string): void {
    this.addQuery.set(value);
  }

  protected addKatalogArtikel(
    artikel: InventurKatalogArtikel
  ): void {
    if (this.isReadOnly()) return;

    // Add-Artikel may add an article only once. Additional units are handled
    // from the table/numpad, not by adding the same article again.
    if (
      this.artikel().some(
        (entry) => entry.artikelnummer === artikel.artikelnummer
      )
    ) {
      this.closeAddDialog();
      return;
    }

    const initialUnit =
      artikel.verfuegbareEinheiten.find(
        (option) =>
          option.inventurEinheit === artikel.inventurEinheit
      ) ?? artikel.verfuegbareEinheiten[0];

    if (!initialUnit) {
      return;
    }

    const previousNewlyAddedArticleId =
      this.newlyAddedArticleId();

    const neuerArtikel: InventoryArticle = {
      id: this.createArtikelId(
        artikel.artikelnummer
      ),
      positionId: null,
      warengruppe: artikel.warengruppe,
      artikelnummer: artikel.artikelnummer,
      artikelname: artikel.artikelname,
      verkaufspreis: initialUnit.verkaufspreis,
      sollBestand: 0,
      istBestand: null,
      inventurEinheit: initialUnit.inventurEinheit,
      inventurEinheitId: initialUnit.id ?? this.inventoryService.unitIdFromLabel(initialUnit.inventurEinheit),
      umrechnungsfaktor: initialUnit.umrechnungsfaktor,
      grundeinheit: artikel.grundeinheit,
      verfuegbareEinheiten: artikel.verfuegbareEinheiten,
      letzteAenderungZeit: null,
      letzteAenderungBenutzer: null,
    };

    this.pushCreatedUndo(
      neuerArtikel.id,
      previousNewlyAddedArticleId
    );

    // Keep the newly added article in the exact position dictated by the
    // currently active table sort. The visible computed list is sorted too,
    // but sorting the backing state here avoids a one-frame append-at-the-end
    // before Angular renders the row at its final position.
    this.artikel.update((articles) =>
      this.sortArtikel([
        ...articles,
        neuerArtikel,
      ])
    );

    this.newlyAddedArticleId.set(
      neuerArtikel.id
    );

    this.closeAddDialog();
    this.openNumpad(
      neuerArtikel
    );
    this.scrollArticleIntoView(
      neuerArtikel.id
    );
  }

  private createArtikelId(
    artikelnummer: string
  ): string {
    const taken = new Set(
      this.artikel().map((entry) => entry.id)
    );

    if (!taken.has(artikelnummer)) {
      return artikelnummer;
    }

    let suffix = 2;

    while (
      taken.has(
        `${artikelnummer}#neu${suffix}`
      )
    ) {
      suffix++;
    }

    return `${artikelnummer}#neu${suffix}`;
  }

  protected selectEinheit(value: string): void {
    this.filterEinheit.set(value);
    this.isEinheitDropdownOpen.set(false);
  }

  protected toggleCompact(): void {
    this.isCompact.update((state) => !state);
  }

  protected setSort(column: SortColumn): void {
    const current = this.sortState();

    // 1. unsorted -> ASC
    if (current.column !== column) {
      this.sortState.set({
        column,
        direction: 'asc',
      });

      return;
    }

    // 2. ASC -> DESC
    if (current.direction === 'asc') {
      this.sortState.set({
        column,
        direction: 'desc',
      });

      return;
    }

    // 3. DESC -> Default / Backend order
    this.sortState.set({
      column: null,
      direction: 'asc',
    });
  }

  protected isSortedBy(column: SortColumn): boolean {
    return this.sortState().column === column;
  }

  protected getSortIcon(column: SortColumn): string {
    if (!this.isSortedBy(column)) {
      return 'unfold_more';
    }

    return this.sortState().direction === 'asc'
      ? 'keyboard_arrow_up'
      : 'keyboard_arrow_down';
  }

  protected istSelektierbar(
    entry: InventoryArticle
  ): boolean {
    return !this.istErfasst(entry);
  }

  protected toggleSelection(
    artikelId: string
  ): void {
    const entry =
      this.artikel().find(
        (current) =>
          current.id === artikelId
      );

    if (
      !entry ||
      !this.istSelektierbar(entry)
    ) {
      return;
    }

    this.selection.update((current) => {
      const next = new Set(current);

      if (next.has(artikelId)) {
        next.delete(artikelId);
      } else {
        next.add(artikelId);
      }

      return next;
    });
  }

  protected isSelected(
    artikelId: string
  ): boolean {
    return this.selection().has(
      artikelId
    );
  }

  protected toggleSelectAllFiltered(): void {
    const filtered =
      this.selektierbareArtikel();

    const allSelected =
      this.areAllFilteredSelected();

    this.selection.update((current) => {
      const next = new Set(current);

      for (const entry of filtered) {
        if (allSelected) {
          next.delete(entry.id);
        } else {
          next.add(entry.id);
        }
      }

      return next;
    });
  }

  protected clearSelection(): void {
    this.selection.set(
      new Set<string>()
    );
  }

  protected bulkSetNull(): void {
    if (this.isReadOnly()) return;

    this.applyToSelection(() => 0);
  }

  protected openNumpad(
    entry: InventoryArticle
  ): void {
    if (this.isReadOnly()) return;

    this.activeId.set(entry.id);
    this.activeUnit.set(
      entry.inventurEinheit
    );
    this.inputValue.set('');
    this.placeholderValue.set(
      entry.istBestand === null
        ? ''
        : String(
          Math.round(
            entry.istBestand
          )
        )
    );
  }

  protected closeNumpad(): void {
    this.activeId.set(null);
    this.inputValue.set('');
    this.placeholderValue.set('');
  }

  protected isNumpadOpenFor(
    entry: InventoryArticle
  ): boolean {
    return this.activeId() === entry.id;
  }

  private readonly einheitBesitzer = computed(() => {
    const index =
      new Map<
        string,
        Map<InventurEinheit, string>
      >();

    for (const entry of this.artikel()) {
      let einheiten =
        index.get(entry.artikelnummer);

      if (!einheiten) {
        einheiten =
          new Map<
            InventurEinheit,
            string
          >();

        index.set(
          entry.artikelnummer,
          einheiten
        );
      }

      einheiten.set(
        entry.inventurEinheit,
        entry.id
      );
    }

    return index;
  });

  private belegtVon(
    entry: InventoryArticle,
    einheit: InventurEinheit
  ): string | null {
    const besitzer =
      this.einheitBesitzer()
        .get(entry.artikelnummer)
        ?.get(einheit);

    return (
      besitzer !== undefined &&
        besitzer !== entry.id
        ? besitzer
        : null
    );
  }

  protected istEinheitBelegt(
    entry: InventoryArticle,
    einheit: InventurEinheit
  ): boolean {
    return (
      this.belegtVon(
        entry,
        einheit
      ) !== null
    );
  }

  protected pressNumpadKey(
    key: string | null
  ): void {
    if (key === null) {
      return;
    }

    if (key === NUMPAD_BACKSPACE) {
      this.inputValue.update(
        (value) =>
          value.slice(
            0,
            -1
          )
      );

      return;
    }

    this.inputValue.update(
      (value) =>
        value + key
    );
  }

  protected stepInput(
    delta: number
  ): void {
    this.inputValue.update((value) => {
      const raw =
        value !== ''
          ? value
          : this.placeholderValue();

      const parsed =
        Number.parseInt(
          raw || '0',
          10
        );

      const base =
        Number.isNaN(parsed)
          ? 0
          : parsed;

      return String(
        Math.max(
          0,
          base + delta
        )
      );
    });
  }

  protected onNumpadInput(
    element: HTMLInputElement
  ): void {
    const sanitized =
      element.value.replace(
        /[^0-9]/g,
        ''
      );

    if (
      element.value !==
      sanitized
    ) {
      element.value =
        sanitized;
    }

    this.inputValue.set(
      sanitized
    );
  }

  protected selectUnit(
    unit: InventurEinheit
  ): void {
    const active =
      this.activeArtikel();

    if (
      active === null ||
      this.findUnitOption(
        active,
        unit
      ) === null
    ) {
      return;
    }

    if (
      unit === this.activeUnit()
    ) {
      return;
    }

    const draft =
      this.inputValue().trim();

    if (draft !== '') {
      const parsed =
        Math.round(
          Number.parseFloat(
            draft.replace(',', '.')
          )
        );

      if (
        Number.isNaN(parsed) ||
        parsed < 0
      ) {
        return;
      }

      const touched =
        this.commitMenge(
          active.id,
          parsed,
          this.activeUnit()
        );

      if (touched !== null) {
        this.flashRow(touched);
      }
    }

    const targetRow =
      this.artikel().find(
        (current) =>
          current.artikelnummer ===
          active.artikelnummer &&
          current.inventurEinheit ===
          unit
      );

    this.activeUnit.set(unit);

    if (targetRow) {
      this.activeId.set(
        targetRow.id
      );
    }

    this.inputValue.set('');

    this.placeholderValue.set(
      targetRow?.istBestand == null
        ? ''
        : String(
          Math.round(
            targetRow.istBestand
          )
        )
    );
  }

  protected unitRow(
    unit: InventurEinheit
  ): InventoryArticle | undefined {
    const active =
      this.activeArtikel();

    if (active === null) {
      return undefined;
    }

    return this.artikel().find(
      (current) =>
        current.artikelnummer ===
        active.artikelnummer &&
        current.inventurEinheit ===
        unit
    );
  }

  protected isUnitErfasst(
    unit: InventurEinheit
  ): boolean {
    return (
      this.unitRow(unit)
        ?.istBestand != null
    );
  }

  protected unitMenge(
    unit: InventurEinheit
  ): number | null {
    return (
      this.unitRow(unit)
        ?.istBestand ??
      null
    );
  }

  protected liveWert(): number | null {
    const option =
      this.activeUnitOption();

    const raw =
      this.inputValue().trim() !== ''
        ? this.inputValue()
        : this.placeholderValue();

    const parsed =
      Number.parseInt(
        raw,
        10
      );

    if (
      option === null ||
      Number.isNaN(parsed)
    ) {
      return null;
    }

    return (
      parsed *
      option.verkaufspreis
    );
  }

  protected weitereEinheit(): void {
    const active =
      this.activeArtikel();

    if (active === null) {
      return;
    }

    const raw =
      this.inputValue().trim();

    if (raw !== '') {
      const parsed =
        Math.round(
          Number.parseFloat(
            raw.replace(',', '.')
          )
        );

      if (
        Number.isNaN(parsed) ||
        parsed < 0
      ) {
        return;
      }

      const touched =
        this.commitMenge(
          active.id,
          parsed,
          this.activeUnit()
        );

      if (touched) {
        this.flashRow(
          touched
        );
      }
    }

    const units =
      active.verfuegbareEinheiten.map(
        (option) =>
          option.inventurEinheit
      );

    if (units.length === 0) {
      return;
    }

    const currentIndex =
      units.indexOf(
        this.activeUnit()
      );

    const nextIndex =
      currentIndex === -1
        ? 0
        : (
          currentIndex + 1
        ) % units.length;

    const nextUnit =
      units[nextIndex];

    const nextRow =
      this.artikel().find(
        (current) =>
          current.artikelnummer ===
          active.artikelnummer &&
          current.inventurEinheit ===
          nextUnit
      );

    this.activeUnit.set(
      nextUnit
    );

    if (nextRow) {
      this.activeId.set(
        nextRow.id
      );
    }

    this.inputValue.set('');

    this.placeholderValue.set(
      nextRow?.istBestand == null
        ? ''
        : String(
          Math.round(
            nextRow.istBestand
          )
        )
    );
  }

  protected confirmInput(): void {
    if (this.isReadOnly()) {
      this.closeNumpad();
      return;
    }

    const active =
      this.activeArtikel();

    if (active === null) {
      return;
    }

    const raw =
      this.inputValue().trim() !== ''
        ? this.inputValue()
        : this.placeholderValue();

    const parsed =
      Math.round(
        Number.parseFloat(
          raw.replace(',', '.')
        )
      );

    const hasNothingToCommit =
      Number.isNaN(parsed) ||
      parsed < 0;

    if (hasNothingToCommit) {
      const next =
        this.nextVisibleArtikel(
          active.id
        );

      if (next) {
        this.openNumpad(next);
      } else {
        this.closeNumpad();
      }

      return;
    }

    this.finalizeCommit(
      active.id,
      parsed
    );
  }

  protected changeEinheit(
    entry: InventoryArticle,
    einheit: InventurEinheit
  ): void {
    if (this.isReadOnly()) return;

    if (
      einheit ===
      entry.inventurEinheit
    ) {
      return;
    }

    const option =
      this.findUnitOption(
        entry,
        einheit
      );

    if (
      option === null ||
      this.istEinheitBelegt(
        entry,
        einheit
      )
    ) {
      return;
    }

    this.pushUndo(entry);

    this.artikel.update(
      (articles) =>
        articles.map(
          (current) =>
            current.id ===
              entry.id
              ? {
                ...current,
                inventurEinheit:
                  option.inventurEinheit,
                inventurEinheitId:
                  option.id ?? this.inventoryService.unitIdFromLabel(option.inventurEinheit),
                verkaufspreis:
                  option.verkaufspreis,
                umrechnungsfaktor:
                  option.umrechnungsfaktor,
                sollBestand:
                  this.convertSollBestand(
                    entry,
                    option.umrechnungsfaktor
                  ),
              }
              : current
        )
    );

    if (entry.istBestand !== null) {
      this.persistPosition(entry.id);
    }

    if (
      this.isNumpadOpenFor(
        entry
      )
    ) {
      this.activeUnit.set(
        option.inventurEinheit
      );

      this.inputValue.set('');

      this.placeholderValue.set(
        entry.istBestand === null
          ? ''
          : String(
            Math.round(
              entry.istBestand
            )
          )
      );
    }

    this.flashUnitMeta(
      entry.id
    );
  }

  protected undo(): void {
    const stack =
      this.undoStack();

    if (stack.length === 0) {
      return;
    }

    const last =
      stack[
      stack.length - 1
      ];

    if (last.kind === 'create') {
      this.artikel.update(
        (articles) =>
          articles.filter(
            (current) =>
              current.id !==
              last.artikelId
          )
      );

      this.selection.update(
        (current) => {
          if (
            !current.has(
              last.artikelId
            )
          ) {
            return current;
          }

          const next =
            new Set(current);

          next.delete(
            last.artikelId
          );

          return next;
        }
      );

      if (
        this.activeId() ===
        last.artikelId
      ) {
        this.closeNumpad();
      }

      this.restoreNewArticleHighlight(
        last.previousNewlyAddedArticleId
      );
    } else if (
      last.kind === 'batch'
    ) {
      const snapshots =
        new Map<string, UndoUpdateEntry>(
          last.entries.map(
            (entry) =>
              [
                entry.artikelId,
                entry,
              ] as const
          )
        );

      this.artikel.update(
        (articles) =>
          articles.map(
            (current) => {
              const snapshot =
                snapshots.get(
                  current.id
                );

              if (!snapshot) {
                return current;
              }

              return this.restoreUndoEntry(
                current,
                snapshot
              );
            }
          )
      );

      const availableIds =
        new Set(
          this.artikel().map(
            (entry) => entry.id
          )
        );

      this.selection.set(
        new Set(
          last.selectedIds.filter(
            (id) =>
              availableIds.has(id)
          )
        )
      );

      this.restoreNewArticleHighlight(
        last.previousNewlyAddedArticleId
      );
    } else {
      this.artikel.update(
        (articles) =>
          articles.map(
            (current) =>
              current.id === last.artikelId
                ? this.restoreUndoEntry(
                  current,
                  last
                )
                : current
          )
      );

      this.restoreNewArticleHighlight(
        last.previousNewlyAddedArticleId
      );

      if (
        this.activeId() ===
        last.artikelId
      ) {
        const restored =
          this.artikel().find(
            (entry) =>
              entry.id ===
              last.artikelId
          );

        if (restored) {
          this.activeUnit.set(
            restored.inventurEinheit
          );

          this.inputValue.set('');

          this.placeholderValue.set(
            restored.istBestand === null
              ? ''
              : String(
                Math.round(
                  restored.istBestand
                )
              )
          );
        }
      }
    }

    this.undoStack.set(
      stack.slice(
        0,
        -1
      )
    );
  }

  protected openSummary(): void {
    if (!this.canFinish()) {
      return;
    }

    this.isEinheitDropdownOpen.set(false);
    this.closeNumpad();
    this.summaryReviewed.set(false);
    this.closeError.set(false);
    this.isSummaryOpen.set(true);
  }

  protected closeSummary(): void {
    if (this.isClosing()) {
      return;
    }

    this.isSummaryOpen.set(false);
  }

  protected onSummaryScroll(
    element: HTMLElement
  ): void {
    this.syncSummaryReviewed(
      element
    );
  }

  private syncSummaryReviewed(
    element: HTMLElement
  ): void {
    if (this.summaryReviewed()) {
      return;
    }

    const remaining =
      element.scrollHeight -
      element.clientHeight -
      element.scrollTop;

    if (
      remaining <=
      SUMMARY_SCROLL_TOLERANCE
    ) {
      this.summaryReviewed.set(
        true
      );
    }
  }

  protected confirmClose(): void {
    const detail = this.detail();

    if (!detail || !this.canConfirmClose()) {
      return;
    }

    const id = detail.id;
    const filialNummer = detail.filialNummer;

    this.isClosing.set(true);
    this.closeError.set(false);

    this.inventoryService
      .heartbeatInventoryLock(id, filialNummer)
      .pipe(
        switchMap(() =>
          this.inventoryService.closeInventory(
            id,
            filialNummer,
            this.anzahlOffen() > 0
          )
        ),
        finalize(() => this.isClosing.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.isSummaryOpen.set(false);
          this.lockedInventoryId = null;
          this.inventoryService.clearInventoryLock(id, filialNummer);
          void this.router.navigate(['/inventur']);
        },
        error: (error: unknown) => {
          if (!this.handleMutationLockError(id, error)) {
            this.closeError.set(true);
          }
        },
      });
  }

  protected getStatusLabel(
    status: InventoryStatus
  ): string {
    switch (status) {
      case InventoryStatus.Offen:
        return 'Offen';

      case InventoryStatus.InBearbeitung:
        return 'In Bearbeitung';

      case InventoryStatus.Erfasst:
        return 'Erfasst';

      case InventoryStatus.Abgeschlossen:
        return 'Abgeschlossen';
    }
  }

  protected getStatusClass(
    status: InventoryStatus
  ): string {
    switch (status) {
      case InventoryStatus.Offen:
        return 'status-offen';

      case InventoryStatus.InBearbeitung:
        return 'status-in-bearbeitung';

      case InventoryStatus.Erfasst:
        return 'status-erfasst';

      case InventoryStatus.Abgeschlossen:
        return 'status-abgeschlossen';
    }
  }

  protected getEinheitKuerzel(
    einheit: InventurEinheit
  ): string {
    const direct = EINHEIT_KUERZEL[einheit];
    if (direct) return direct;

    const normalized = einheit.trim().toLocaleUpperCase('de-DE');

    if (normalized.startsWith('STÜCK') || normalized.startsWith('STUECK')) return 'STK';
    if (normalized.startsWith('KAR')) return 'KAR';
    if (normalized.startsWith('KIS')) return 'KIS';
    if (normalized.startsWith('BEU')) return 'BEU';
    if (normalized.startsWith('KG') || normalized.includes('KILO')) return 'KG';

    return einheit;
  }

  protected istErfasst(
    entry: InventoryArticle
  ): boolean {
    return (
      this.getLiveIst(
        entry
      ) !== null
    );
  }

  protected getIstText(
    entry: InventoryArticle
  ): string {
    const ist = this.getLiveIst(entry);

    return (
      ist === null
        ? '—'
        : String(
          Math.round(
            ist
          )
        )
    );
  }

  protected getLiveIst(
    entry: InventoryArticle
  ): number | null {
    if (
      this.activeId() !== entry.id ||
      this.activeUnit() !==
      entry.inventurEinheit
    ) {
      return entry.istBestand;
    }

    const parsed =
      Number.parseInt(
        this.inputValue(),
        10
      );

    return (
      Number.isNaN(parsed) ||
        parsed < 0
        ? entry.istBestand
        : parsed
    );
  }

  protected getWertText(
    entry: InventoryArticle
  ): string {
    const ist =
      this.getLiveIst(entry);

    if (ist === null) {
      return '—';
    }

    return `${this.formatBetrag(
      ist *
      entry.verkaufspreis
    )} €`;
  }

  protected getFaktorText(
    entry: InventoryArticle
  ): string {
    return this.formatFaktor(
      entry.umrechnungsfaktor
    );
  }

  protected formatFaktor(
    umrechnungsfaktor: number
  ): string {
    if (
      umrechnungsfaktor <= 0
    ) {
      return '—';
    }

    return (
      1 /
      umrechnungsfaktor
    ).toLocaleString(
      'de-DE',
      {
        minimumFractionDigits: 0,
        maximumFractionDigits: 4,
      }
    );
  }

  protected formatBetrag(
    value: number
  ): string {
    return new Intl.NumberFormat(
      'de-DE',
      {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }
    ).format(value);
  }

  protected formatMenge(
    value: number
  ): string {
    return new Intl.NumberFormat(
      'de-DE',
      {
        maximumFractionDigits: 2,
      }
    ).format(value);
  }

  protected artikelWert(
    entry: InventoryArticle
  ): number {
    return (
      (
        entry.istBestand ??
        0
      ) *
      entry.verkaufspreis
    );
  }

  private applyInitialAnsicht(): void {
    const action =
      (
        this.route.snapshot
          .queryParamMap
          .get('action') ??
        ''
      ).toLowerCase();

    if (action === 'weiter') {
      this.ansichtFilter.set(
        'OFFEN'
      );
    }
  }

  private moveActiveRow(
    event: KeyboardEvent,
    offset: number
  ): void {
    const currentId =
      this.activeId();

    if (currentId === null) {
      return;
    }

    event.preventDefault();

    const visible =
      this.gefilterteArtikel();

    const nextIndex =
      visible.findIndex(
        (entry) =>
          entry.id === currentId
      ) +
      offset;

    if (
      nextIndex < 0 ||
      nextIndex >= visible.length
    ) {
      return;
    }

    this.openNumpad(
      visible[nextIndex]
    );
  }

  private sortArtikel(
    entries: InventoryArticle[]
  ): InventoryArticle[] {
    const {
      column,
      direction,
    } =
      this.sortState();

    if (column === null) {
      // Keep the default inventory order explicit on the client as well.
      // This is especially important for locally added articles: without an
      // active manual sort they must still move to their real Artikelnummer
      // position instead of remaining at the end of the backing array.
      return [...entries].sort((first, second) =>
        first.artikelnummer.localeCompare(
          second.artikelnummer,
          'de',
          {
            sensitivity: 'base',
            numeric: true,
          }
        )
      );
    }

    const factor =
      direction === 'asc'
        ? 1
        : -1;

    return [
      ...entries,
    ].sort(
      (
        first,
        second
      ) => {
        switch (column) {
          case 'warengruppe': {
            const byWarengruppe =
              first.warengruppe.localeCompare(
                second.warengruppe,
                'de'
              );

            return (
              (
                byWarengruppe !== 0
                  ? byWarengruppe
                  : first.artikelname.localeCompare(
                    second.artikelname,
                    'de'
                  )
              ) *
              factor
            );
          }

          case 'artikelnummer':
            return (
              first.artikelnummer.localeCompare(
                second.artikelnummer,
                'de',
                {
                  sensitivity: 'base',
                  numeric: true,
                }
              ) *
              factor
            );

          case 'artikelname':
            return (
              first.artikelname.localeCompare(
                second.artikelname,
                'de'
              ) *
              factor
            );

          default:
            return 0;
        }
      }
    );
  }

  private matchesAnsicht(
    entry: InventoryArticle,
    ansicht: AnsichtFilter
  ): boolean {
    switch (ansicht) {
      case 'ALLE':
        return true;

      case 'OFFEN':
        return (
          entry.istBestand === null
        );

      case 'ERFASST':
        return (
          entry.istBestand !== null
        );
    }
  }

  private applyToSelection(
    nextValue:
      (
        entry: InventoryArticle
      ) => number
  ): void {
    const selection =
      this.selection();

    if (selection.size === 0) {
      return;
    }

    const undoEntries:
      UndoUpdateEntry[] = [];

    const previousNewlyAddedArticleId =
      this.newlyAddedArticleId();

    const changedIds: string[] = [];

    this.artikel.update(
      (articles) =>
        articles.map(
          (current) => {
            if (
              !selection.has(
                current.id
              )
            ) {
              return current;
            }

            const value = nextValue(current);

            if (current.istBestand === value) {
              return current;
            }

            undoEntries.push(this.toUndoEntry(current));
            changedIds.push(current.id);

            return {
              ...current,
              istBestand: value,
              letzteAenderungZeit:
                this.nowTime(),
            } satisfies InventoryArticle;
          }
        )
    );

    if (undoEntries.length > 0) {
      this.pushBatchUndo(
        undoEntries,
        changedIds,
        previousNewlyAddedArticleId
      );
    }

    if (
      previousNewlyAddedArticleId !== null &&
      changedIds.includes(
        previousNewlyAddedArticleId
      )
    ) {
      this.newlyAddedArticleId.set(
        null
      );
    }

    this.clearSelection();

    if (changedIds.length > 0) {
      this.persistPositions(changedIds);
    }
  }

  private finalizeCommit(
    artikelId: string,
    value: number
  ): void {
    const next =
      this.nextVisibleArtikel(
        artikelId
      );

    const touched =
      this.commitMenge(
        artikelId,
        value,
        this.activeUnit()
      );

    if (touched !== null) {
      this.flashRow(
        touched
      );
    }

    if (next) {
      this.openNumpad(
        next
      );
    } else {
      this.closeNumpad();
    }
  }

  private commitMenge(
    artikelId: string,
    value: number,
    unit: InventurEinheit
  ): string | null {
    const base =
      this.artikel().find(
        (current) =>
          current.id === artikelId
      );

    if (!base) {
      return null;
    }

    const option =
      this.findUnitOption(
        base,
        unit
      );

    if (option === null) {
      return null;
    }

    if (
      base.inventurEinheit === unit
    ) {
      this.commitIst(
        base.id,
        value
      );

      this.clearNewArticleHighlight(
        base
      );

      return base.id;
    }

    const sibling =
      this.artikel().find(
        (current) =>
          current.id !== base.id &&
          current.artikelnummer ===
          base.artikelnummer &&
          current.inventurEinheit ===
          unit
      );

    if (sibling) {
      this.commitIst(
        sibling.id,
        value
      );

      this.clearNewArticleHighlight(
        base
      );

      return sibling.id;
    }

    const neueZeile:
      InventoryArticle = {
      ...base,
      positionId: null,

      id:
        this.createUnitRowId(
          base.id,
          unit
        ),

      inventurEinheit:
        option.inventurEinheit,

      inventurEinheitId:
        option.id ?? this.inventoryService.unitIdFromLabel(option.inventurEinheit),

      verkaufspreis:
        option.verkaufspreis,

      umrechnungsfaktor:
        option.umrechnungsfaktor,

      sollBestand:
        this.convertSollBestand(
          base,
          option.umrechnungsfaktor
        ),

      istBestand:
        Math.round(value),

      letzteAenderungZeit:
        this.nowTime(),
    };

    this.artikel.update(
      (articles) => {
        const index =
          articles.findIndex(
            (current) =>
              current.id === base.id
          );

        const next = [
          ...articles,
        ];

        next.splice(
          index + 1,
          0,
          neueZeile
        );

        return next;
      }
    );

    this.pushCreatedUndo(
      neueZeile.id
    );

    this.persistPosition(neueZeile.id);

    this.clearNewArticleHighlight(
      base
    );

    return neueZeile.id;
  }

  private createUnitRowId(
    baseId: string,
    unit: InventurEinheit
  ): string {
    const taken =
      new Set(
        this.artikel().map(
          (entry) => entry.id
        )
      );

    const base =
      `${baseId}#${unit}`;

    if (!taken.has(base)) {
      return base;
    }

    let suffix = 2;

    while (
      taken.has(
        `${base}#${suffix}`
      )
    ) {
      suffix++;
    }

    return `${base}#${suffix}`;
  }

  private commitIst(
    artikelId: string,
    value: number
  ): void {
    const entry =
      this.artikel().find(
        (current) =>
          current.id === artikelId
      );

    if (!entry) {
      return;
    }

    this.pushUndo(entry);

    this.artikel.update(
      (articles) =>
        articles.map(
          (current) =>
            current.id === artikelId
              ? {
                ...current,
                istBestand:
                  Math.round(value),
                letzteAenderungZeit:
                  this.nowTime(),
              }
              : current
        )
    );

    this.selection.update(
      (current) => {
        if (
          !current.has(
            artikelId
          )
        ) {
          return current;
        }

        const next =
          new Set(current);

        next.delete(
          artikelId
        );

        return next;
      }
    );

    this.persistPosition(artikelId);
  }

  private persistPosition(artikelId: string): void {
    this.persistPositions([artikelId]);
  }

  private persistPositions(artikelIds: readonly string[]): void {
    if (this.isReadOnly()) return;

    for (const artikelId of artikelIds) {
      this.pendingSaveArtikelIds.add(artikelId);
    }

    this.flushPendingPositionSaves();
  }

  private flushPendingPositionSaves(): void {
    if (this.saveInFlight || this.pendingSaveArtikelIds.size === 0 || this.isReadOnly()) {
      return;
    }

    const detail = this.detail();
    if (!detail) {
      this.pendingSaveArtikelIds.clear();
      return;
    }

    const artikelIds = [...this.pendingSaveArtikelIds];
    this.pendingSaveArtikelIds.clear();

    const entries = artikelIds.flatMap((artikelId) => {
      const entry = this.artikel().find((current) => current.id === artikelId);
      const artikelnummer = Number(entry?.artikelnummer);

      if (
        !entry ||
        entry.istBestand === null ||
        !Number.isInteger(artikelnummer)
      ) {
        return [];
      }

      return [{
        artikelId,
        request: {
          id: entry.positionId ?? null,
          artikelnummer,
          inventurMenge: entry.istBestand,
          inventurEinheitId: entry.inventurEinheitId,
        },
      }];
    });

    if (entries.length === 0) {
      return;
    }

    this.saveInFlight = true;
    this.isSavingPositions.set(true);

    this.inventoryService
      .savePositions(
        detail.id,
        detail.filialNummer,
        entries.map((entry) => entry.request)
      )
      .subscribe({
        next: (saved) => {
          this.saveInFlight = false;
          this.isSavingPositions.set(false);
          this.applySavedPositionIds(entries, saved);
          this.refreshInventoryLock(detail.id);
          this.flushPendingPositionSaves();
        },
        error: (error: unknown) => {
          this.saveInFlight = false;
          this.isSavingPositions.set(false);
          this.pendingSaveArtikelIds.clear();

          if (!this.handleMutationLockError(detail.id, error)) {
            this.loadInventoryDetail(detail.id);
          }
        },
      });
  }

  private applySavedPositionIds(
    entries: readonly {
      artikelId: string;
      request: {
        artikelnummer: number;
        inventurEinheitId: number;
      };
    }[],
    saved: readonly {
      id: string | null;
      artikelnummer: number;
      inventurEinheitId: number;
    }[]
  ): void {
    const artikelIds = new Map<string, string>();

    for (const entry of entries) {
      const result = saved.find(
        (item) =>
          item.artikelnummer === entry.request.artikelnummer &&
          item.inventurEinheitId === entry.request.inventurEinheitId
      );

      if (result?.id) {
        artikelIds.set(entry.artikelId, result.id);
      }
    }

    if (artikelIds.size === 0) return;

    this.artikel.update((articles) =>
      articles.map((current) => {
        const id = artikelIds.get(current.id);
        return id ? { ...current, positionId: id } : current;
      })
    );
  }

  private nextVisibleArtikel(
    fromId: string
  ): InventoryArticle | null {
    const visible =
      this.gefilterteArtikel();

    const index =
      visible.findIndex(
        (entry) =>
          entry.id === fromId
      );

    return (
      index === -1
        ? null
        : (
          visible[
          index + 1
          ] ??
          null
        )
    );
  }

  private flashRow(
    artikelId: string
  ): void {
    this.flashId.set(
      artikelId
    );

    window.setTimeout(
      () => {
        this.flashId.update(
          (current) =>
            current === artikelId
              ? null
              : current
        );
      },
      550
    );
  }

  private flashUnitMeta(
    artikelId: string
  ): void {
    this.unitMetaFlashId.set(
      artikelId
    );

    window.setTimeout(
      () => {
        this.unitMetaFlashId.update(
          (current) =>
            current === artikelId
              ? null
              : current
        );
      },
      900
    );
  }

  private findUnitOption(
    entry: InventoryArticle,
    unit: InventurEinheit
  ): InventoryUnitOption | null {
    return (
      entry.verfuegbareEinheiten.find(
        (option) =>
          option.inventurEinheit ===
          unit
      ) ??
      null
    );
  }

  private convertSollBestand(
    entry: InventoryArticle,
    targetFaktor: number
  ): number {
    if (
      entry.umrechnungsfaktor <= 0 ||
      targetFaktor <= 0
    ) {
      return entry.sollBestand;
    }

    const sollInGrundeinheiten =
      entry.sollBestand *
      entry.umrechnungsfaktor;

    return Number(
      (
        sollInGrundeinheiten /
        targetFaktor
      ).toFixed(2)
    );
  }

  private pushUndo(
    entry: InventoryArticle
  ): void {
    const undoEntry:
      UndoUpdateEntry =
      this.toUndoEntry(
        entry
      );

    this.undoStack.update(
      (stack) =>
        [
          ...stack,
          undoEntry,
        ].slice(
          -50
        )
    );
  }

  private pushCreatedUndo(
    artikelId: string,
    previousNewlyAddedArticleId:
      string | null =
      this.newlyAddedArticleId()
  ): void {
    const undoEntry:
      UndoCreateEntry = {
      kind: 'create',
      artikelId,
      previousNewlyAddedArticleId,
    };

    this.undoStack.update(
      (stack) =>
        [
          ...stack,
          undoEntry,
        ].slice(
          -50
        )
    );
  }

  private pushBatchUndo(
    entries:
      readonly UndoUpdateEntry[],
    selectedIds:
      readonly string[],
    previousNewlyAddedArticleId:
      string | null
  ): void {
    const undoEntry:
      UndoBatchEntry = {
      kind: 'batch',
      entries,
      selectedIds,
      previousNewlyAddedArticleId,
    };

    this.undoStack.update(
      (stack) =>
        [
          ...stack,
          undoEntry,
        ].slice(
          -50
        )
    );
  }

  private restoreUndoEntry(
    current: InventoryArticle,
    snapshot: UndoUpdateEntry
  ): InventoryArticle {
    return {
      ...current,

      istBestand:
        snapshot.istBestand,

      sollBestand:
        snapshot.sollBestand,

      inventurEinheit:
        snapshot.inventurEinheit,

      inventurEinheitId:
        snapshot.inventurEinheitId,

      verkaufspreis:
        snapshot.verkaufspreis,

      umrechnungsfaktor:
        snapshot.umrechnungsfaktor,

      letzteAenderungZeit:
        snapshot.letzteAenderungZeit,

      letzteAenderungBenutzer:
        snapshot.letzteAenderungBenutzer,
    };
  }

  private toUndoEntry(
    entry: InventoryArticle
  ): UndoUpdateEntry {
    return {
      kind: 'update',

      artikelId:
        entry.id,

      istBestand:
        entry.istBestand,

      sollBestand:
        entry.sollBestand,

      inventurEinheit:
        entry.inventurEinheit,

      inventurEinheitId:
        entry.inventurEinheitId,

      verkaufspreis:
        entry.verkaufspreis,

      umrechnungsfaktor:
        entry.umrechnungsfaktor,

      letzteAenderungZeit:
        entry.letzteAenderungZeit,

      letzteAenderungBenutzer:
        entry.letzteAenderungBenutzer,

      previousNewlyAddedArticleId:
        this.newlyAddedArticleId(),
    };
  }

  private clearNewArticleHighlight(
    entry: InventoryArticle
  ): void {
    const highlightedId =
      this.newlyAddedArticleId();

    if (highlightedId === null) {
      return;
    }

    const highlighted =
      this.artikel().find(
        (current) =>
          current.id === highlightedId
      );

    if (
      highlighted?.artikelnummer ===
      entry.artikelnummer
    ) {
      this.newlyAddedArticleId.set(
        null
      );
    }
  }

  private restoreNewArticleHighlight(
    artikelId: string | null
  ): void {
    this.newlyAddedArticleId.set(
      this.articleExists(
        artikelId
      )
        ? artikelId
        : null
    );
  }

  private articleExists(
    artikelId: string | null
  ): boolean {
    if (artikelId === null) {
      return false;
    }

    return this.artikel().some(
      (entry) =>
        entry.id === artikelId
    );
  }

  private nowTime(): string {
    const now = new Date();

    return [
      now.getHours(),
      now.getMinutes(),
      now.getSeconds(),
    ]
      .map(
        (part) =>
          String(part).padStart(
            2,
            '0'
          )
      )
      .join(':');
  }

  private scrollArticleIntoView(artikelId: string): void {
    // Angular renders the newly added row on the next frame. A second frame
    // makes sure sorting/filtering and the numpad state have settled first.
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        const container = this.tableBody()?.nativeElement;

        if (!container) {
          return;
        }

        const row = Array.from(
          container.querySelectorAll<HTMLElement>('[data-inventory-row-id]')
        ).find((element) => element.dataset['inventoryRowId'] === artikelId);

        row?.scrollIntoView({
          behavior: 'auto',
          block: 'center',
          inline: 'nearest',
        });
      });
    });
  }

  private openInventoryWithLock(id: string): void {
    const previousId = this.lockedInventoryId;

    if (previousId !== null && previousId !== id) {
      this.lockedInventoryId = null;

      this.inventoryService.releaseInventoryLock(previousId, this.filialNummer).subscribe({
        next: () => this.acquireInventoryLockAndLoad(id),
        error: () => {
          this.inventoryService.clearInventoryLock(previousId, this.filialNummer);
          this.acquireInventoryLockAndLoad(id);
        },
      });

      return;
    }

    this.acquireInventoryLockAndLoad(id);
  }

  private acquireInventoryLockAndLoad(id: string): void {
    if (this.inventoryService.getInventoryLockSession(id, this.filialNummer)) {
      this.lockedInventoryId = id;
      this.loadInventoryDetail(id);
      return;
    }

    this.isLoading.set(true);
    this.loadError.set(false);

    this.inventoryService
      .acquireInventoryLock(id, this.filialNummer)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.lockedInventoryId = id;
          this.loadInventoryDetail(id);
        },
        error: () => {
          this.isLoading.set(false);
          this.loadError.set(true);
          this.detail.set(null);
          this.artikel.set([]);
          void this.router.navigate(['/inventur']);
        },
      });
  }

  private loadInventoryDetail(id: string): void {
    this.isLoading.set(true);
    this.loadError.set(false);

    this.inventoryService
      .getInventoryDetail(id, this.filialNummer)
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: (detail) => {
          this.detail.set(detail);
          this.artikel.set(detail.artikel);
          this.selection.set(new Set<string>());
          this.undoStack.set([]);
          this.newlyAddedArticleId.set(null);
          this.closeNumpad();
          this.applyInitialAnsicht();
        },
        error: () => {
          this.loadError.set(true);

          if (this.lockedInventoryId === id) {
            this.lockedInventoryId = null;
            this.inventoryService.releaseInventoryLock(id, this.filialNummer).subscribe({
              error: () => this.inventoryService.clearInventoryLock(id, this.filialNummer),
            });
          }
        },
      });
  }

  private refreshInventoryLock(id: string): void {
    if (this.lockedInventoryId !== id) {
      return;
    }

    this.inventoryService.heartbeatInventoryLock(id, this.filialNummer).subscribe({
      next: (session) => {
        if (session === null) {
          this.handleLostInventoryLock(id);
        }
      },
      error: () => this.handleLostInventoryLock(id),
    });
  }

  private handleMutationLockError(id: string, error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 423) {
      return false;
    }

    this.handleLostInventoryLock(id);
    return true;
  }

  private handleLostInventoryLock(id: string): void {
    if (this.lockedInventoryId === id) {
      this.lockedInventoryId = null;
    }

    this.inventoryService.clearInventoryLock(id, this.filialNummer);
    void this.router.navigate(['/inventur']);
  }

  private releaseLockAndNavigate(): void {
    const id = this.lockedInventoryId;

    if (id === null) {
      void this.router.navigate(['/inventur']);
      return;
    }

    this.lockedInventoryId = null;

    this.inventoryService.releaseInventoryLock(id, this.filialNummer).subscribe({
      next: () => void this.router.navigate(['/inventur']),
      error: () => {
        this.inventoryService.clearInventoryLock(id, this.filialNummer);
        void this.router.navigate(['/inventur']);
      },
    });
  }

}